<script>
  // The card options' set dropdown (spec "Card options modal"). A set name
  // always comes with its symbol, coloured by the card's rarity there
  // (VISUALS.md), which a native <select> can't draw, so this is a small
  // listbox: a trigger showing the picked set, and a list that opens with a
  // search box (set name or code; Island alone has 221 sets) over every set,
  // newest first, "Any set" on top. A set the picked finish rules out is
  // greyed, never hidden. The search box is a combobox (combobox-keys.js):
  // the arrows move, Enter picks, Escape closes the list (not the dialog).
  // A row asks for its symbol only once it has been in view.
  import { tick } from 'svelte';
  import SetSymbol from '../shared/SetSymbol.svelte';
  import { setOptions, filterSets } from '../../lib/logic/card-options-logic.js';
  import { comboboxKey, activeDescendant } from '../../lib/logic/combobox-keys.js';

  /** @typedef {import('../../lib/logic/card-options-logic.js').Spec} Spec */
  /** @typedef {import('../../lib/logic/card-options-logic.js').SetOption} SetOption */

  /**
   * @type {{
   *   spec: Spec,
   *   sets: import('../../lib/types.js').CardSet[] | null,
   *   onPick: (code: string | null) => void,
   * }}
   */
  let { spec, sets, onPick } = $props();

  const uid = $props.id();
  const listId = `set-list-${uid}`;
  /** @type {SetOption} */
  const ANY = { code: '', name: 'Any set', year: null, rarity: null, on: false, disabled: false };

  let open = $state(false);
  let query = $state('');
  let active = $state(0);
  let trigger = $state(/** @type {HTMLButtonElement | null} */ (null));
  let wrap = $state(/** @type {HTMLElement | null} */ (null));
  let list = $state(/** @type {HTMLElement | null} */ (null));

  const options = $derived(setOptions(spec, sets ?? []));
  // The picked set. One the sets read doesn't hold (or before it is in)
  // still shows, by its code: a narrowed want never reads "Any set".
  const current = $derived(
    options.find((o) => o.on) ??
      (spec.set_code
        ? { ...ANY, code: spec.set_code, name: spec.set_code.toUpperCase(), on: true }
        : null)
  );
  // "Any set" leads the list until a search narrows it.
  const shown = $derived(
    query.trim() ? filterSets(options, query) : [{ ...ANY, on: !spec.set_code }, ...options]
  );

  async function show() {
    query = '';
    open = true;
    active = Math.max(
      0,
      shown.findIndex((o) => o.on)
    );
    await tick();
    scrollToActive();
  }

  /**
   * The search box takes focus as the list opens.
   * @param {HTMLInputElement} node
   */
  function focusOnOpen(node) {
    node.focus();
  }

  function hide({ refocus = false } = {}) {
    open = false;
    if (refocus) trigger?.focus();
  }

  /** @param {SetOption | undefined} o */
  function pick(o) {
    if (!o || o.disabled) return;
    hide({ refocus: true });
    onPick(o.code || null);
  }

  function scrollToActive() {
    list?.querySelector(`#${listId}-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }

  /** @param {KeyboardEvent} e */
  function onSearchKey(e) {
    if (e.key === 'Tab') {
      hide();
      return;
    }
    const hit = comboboxKey(e.key, {
      open,
      active,
      count: shown.length,
      disabled: (i) => shown[i].disabled,
    });
    if (!hit) return;
    e.preventDefault();
    if (hit.stop) e.stopPropagation(); // close the list, not the dialog
    const { action } = hit;
    if (action.type === 'move') {
      active = action.active;
      scrollToActive();
    } else if (action.type === 'pick') {
      pick(shown[active]);
    } else if (action.type === 'close') {
      hide({ refocus: true });
    }
  }

  /** @param {KeyboardEvent} e */
  function onTriggerKey(e) {
    // Only an arrow opening the list is the trigger's own; Enter and Space
    // stay the button's click.
    if (comboboxKey(e.key, { open, active, count: shown.length })?.action.type !== 'open') return;
    e.preventDefault();
    show();
  }

  /** @param {Event & { currentTarget: HTMLInputElement }} e */
  function onInput(e) {
    query = e.currentTarget.value;
    // The first pickable match is ready for Enter (none: -1).
    active = shown.findIndex((o) => !o.disabled);
  }

  /** @param {PointerEvent} e */
  function onOutside(e) {
    if (open && !wrap?.contains(/** @type {Node} */ (e.target))) hide();
  }
</script>

<svelte:window onpointerdown={onOutside} />

<div class="set-select" bind:this={wrap}>
  <button
    bind:this={trigger}
    class="trigger"
    type="button"
    aria-haspopup="listbox"
    aria-expanded={open}
    aria-label="Set"
    disabled={!sets}
    onclick={() => (open ? hide() : show())}
    onkeydown={onTriggerKey}
  >
    <span class="sym"
      >{#if current}<SetSymbol code={current.code} rarity={current.rarity} />{/if}</span
    >
    <span class="name">{current?.name ?? ANY.name}</span>
    {#if current?.year}<span class="year">{current.year}</span>{/if}
    <svg class="chev" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"
      ><path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.6" /></svg
    >
  </button>

  {#if open}
    <div class="panel">
      <input
        class="search"
        type="search"
        role="combobox"
        aria-label="Search sets"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={activeDescendant(listId, open, active)}
        aria-autocomplete="list"
        placeholder="Set name or code"
        autocomplete="off"
        spellcheck="false"
        value={query}
        use:focusOnOpen
        oninput={onInput}
        onkeydown={onSearchKey}
      />
      <ul bind:this={list} id={listId} class="list" role="listbox" aria-label="Sets">
        {#each shown as o, i (o.code)}
          <!-- Keyboard lives on the search box (aria-activedescendant). -->
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <li
            id="{listId}-{i}"
            class="opt"
            class:active={i === active}
            class:on={o.on}
            class:disabled={o.disabled}
            role="option"
            aria-selected={o.on}
            aria-disabled={o.disabled}
            onclick={() => pick(o)}
            onpointerenter={() => !o.disabled && (active = i)}
          >
            <span class="sym"
              >{#if o.code}<SetSymbol code={o.code} rarity={o.rarity} lazy />{/if}</span
            >
            <span class="name">{o.name}</span>
            {#if o.year}<span class="year">{o.year}</span>{/if}
          </li>
        {:else}
          <li class="none" role="presentation">No set matches</li>
        {/each}
      </ul>
    </div>
  {/if}
</div>

<style>
  .set-select {
    position: relative;
  }
  .trigger {
    width: 100%;
    height: 44px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 12px;
    border: 1px solid var(--color-border);
    border-radius: 10px;
    background: var(--color-bg-surface);
    font: inherit;
    font-size: 15px;
    color: var(--color-text);
    text-align: left;
    cursor: pointer;
  }
  .trigger:disabled {
    color: var(--color-text-tertiary);
    cursor: default;
  }
  .trigger:focus-visible,
  .trigger[aria-expanded='true'] {
    outline: none;
    border-color: var(--color-accent-dark);
  }
  .sym {
    display: flex;
    justify-content: center;
    width: 20px;
    flex-shrink: 0;
  }
  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .year {
    font-size: 13px;
    color: var(--color-text-tertiary);
  }
  .chev {
    flex-shrink: 0;
    color: var(--color-text-tertiary);
  }
  /* Over the printings grid, below the trigger. */
  .panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    max-height: min(360px, 55dvh);
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-bg-surface);
    box-shadow: 0 10px 30px rgba(44, 36, 25, 0.2);
  }
  .search {
    flex-shrink: 0;
    margin: 6px 6px 2px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid var(--color-border);
    border-radius: 8px;
    background: var(--color-bg-surface);
    font: inherit;
    /* 16px keeps iOS from zooming into the field. */
    font-size: 16px;
    color: var(--color-text);
  }
  .search:focus {
    outline: none;
    border-color: var(--color-accent-dark);
  }
  .list {
    margin: 0;
    padding: 4px;
    list-style: none;
    overflow-y: auto;
  }
  .opt {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 14px;
    color: var(--color-text);
    cursor: pointer;
  }
  .opt.active {
    background: var(--color-accent-bg);
  }
  .opt.on {
    font-weight: 700;
  }
  /* Ruled out by the picked finish: still listed, greyed, not pickable. */
  .opt.disabled {
    opacity: 0.35;
    cursor: default;
  }
  .none {
    padding: 10px;
    font-size: 14px;
    color: var(--color-text-tertiary);
  }
</style>
